import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrder, updateOrderProduct, fetchOrderNewReference } from '../../../admin/orders-controller/order-controller';
import { fetchSettings } from '../../../admin/settings/controller/settings-controller';
import { useStoreContact } from '../../../../components/templates/shared/useStoreChrome';
import { tunisianCities } from '../../../../utils/cities';
import facebookConversionService from '../../../../services/facebookConversionService';
import ExitOfferPopup from './ExitOfferPopup';

const formatPrice = (n) => Number(n || 0).toFixed(2);

// Customer identity for the Facebook pixel / Conversions API: the full name is split into
// first and last name, the city code is turned into the city name Meta matches on.
const pixelCustomer = ({ name, phone, city }) => {
  const [firstName, ...rest] = String(name || '').trim().split(/\s+/);
  return {
    phone,
    firstName: firstName || undefined,
    lastName: rest.join(' ') || undefined,
    city: tunisianCities.find((c) => c.Code === city)?.EnName,
    country: 'TN',
  };
};

/**
 * "Buy now" product panel: price, a lead-capture form and an order summary,
 * all on the product page itself — no separate cart/checkout step required.
 * Matches the single-page COD layout used across Tunisian dropshipping stores.
 */
const QuickBuyProductInfo = ({
  product,
  selectedVariant,
  quantity,
  onQuantityChange,
  onAddToCart,
  variantTypes,
  onVariantChange,
  selectedPack,
  onPackSelect,
  gallery,
}) => {
  const navigate = useNavigate();
  const { phone: storePhone } = useStoreContact();
  const [deliveryFee, setDeliveryFee] = useState(8);
  const [form, setForm] = useState({ name: '', phone: '', city: 'TUN', address: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  // "Remise" exit offer accepted in the popup: the discount applies to this product's order.
  const [exitOfferAccepted, setExitOfferAccepted] = useState(false);

  // Abandoned-order tracking: the order is created as soon as a full phone number is
  // typed, then patched to "pending" when the customer actually confirms.
  const [abandonedOrderId, setAbandonedOrderId] = useState(null);
  const phoneInputRef = useRef(null);
  const addressInputRef = useRef(null);
  const abandonedOrderCreatedRef = useRef(false);
  const referenceRef = useRef(null);
  const referencePromiseRef = useRef(null);

  const fetchOrderRef = useCallback(() => {
    if (referenceRef.current) return Promise.resolve();
    if (referencePromiseRef.current) return referencePromiseRef.current;
    referencePromiseRef.current = (async () => {
      try {
        const reference = await fetchOrderNewReference();
        referenceRef.current = reference && typeof reference === 'object' && reference.numero
          ? reference.numero
          : `CMD-${Date.now()}`;
      } catch (error) {
        console.error('Error fetching order reference:', error);
        referenceRef.current = `CMD-${Date.now()}`;
      }
    })();
    return referencePromiseRef.current;
  }, []);

  useEffect(() => {
    fetchSettings()
      .then((s) => { if (s?.fraisDeLivraison !== undefined) setDeliveryFee(s.fraisDeLivraison); })
      .catch(() => {});
  }, []);

  // Facebook ViewContent (and the PageView of the product's category pixel).
  useEffect(() => {
    if (!product?.id) return;
    const price = product.enPromo && product.prixVentePromo ? product.prixVentePromo : product.prixVenteTTC;
    facebookConversionService.trackViewContent(
      { productId: product.id, productName: product.name, value: price || 0, currency: 'TND' },
      product.categorie
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // An accepted exit offer belongs to its product only.
  useEffect(() => {
    setExitOfferAccepted(false);
  }, [product?.id]);

  if (!product) return null;

  const regularUnitPrice = selectedVariant?.prixVenteTTC
    ?? (product.enPromo && product.prixVentePromo ? product.prixVentePromo : product.prixVenteTTC) ?? 0;
  const basePrice = selectedPack ? selectedPack.prixPack : regularUnitPrice;
  const exitOfferPercent = product.remiseActive ? Number(product.remisePourcentage) || 0 : 0;
  const exitPct = exitOfferAccepted ? exitOfferPercent : 0;
  const unitPrice = basePrice * (1 - exitPct / 100);
  const originalPrice = product.enPromo && !selectedPack ? product.prixVenteTTC : (exitPct > 0 ? basePrice : null);
  const discountPct = originalPrice ? Math.round((1 - unitPrice / originalPrice) * 100) : 0;

  // Pack savings (vs. the regular per-unit price) and exit offer, for the order's remise accounting.
  const packUnitSavings = selectedPack ? Math.max(0, (product.prixVenteTTC || 0) - selectedPack.prixPack) : 0;
  const packTotalRemise = packUnitSavings * quantity;
  const exitTotalRemise = basePrice * quantity * exitPct / 100;
  const totalRemise = packTotalRemise + exitTotalRemise;
  // Price written on the order line, before the remise.
  const lineUnitPrice = selectedPack ? (product.prixVenteTTC || basePrice) : regularUnitPrice;
  const tauxRemise = lineUnitPrice * quantity > 0
    ? Number(((totalRemise / (lineUnitPrice * quantity)) * 100).toFixed(2))
    : 0;

  const freeDelivery = product.livraisonGratuite && quantity >= (product.seuilLivraisonGratuite || 1);
  const finalDeliveryFee = freeDelivery ? 0 : Number(deliveryFee || 0);
  const subtotal = unitPrice * quantity;
  const total = subtotal + finalDeliveryFee;

  const handlePackClick = (pack) => {
    if (onPackSelect) onPackSelect(pack);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Fetch the next order reference as soon as the customer starts filling the form.
    fetchOrderRef();

    if (name === 'phone') {
      const phoneValue = value.replace(/\D/g, '').slice(0, 8);
      setForm((f) => ({ ...f, phone: phoneValue }));
      if (errors.phone) setErrors((er) => ({ ...er, phone: undefined }));

      // Capture the lead as an abandoned order the moment the 8th digit lands.
      if (phoneValue.length === 8 && !abandonedOrderCreatedRef.current) {
        abandonedOrderCreatedRef.current = true;
        fetchOrderRef().then(() => createAbandonedOrder(phoneValue));
      }
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.phone.trim()) e.phone = 'Numéro de téléphone requis';
    else if (!/^\d{8}$/.test(form.phone.trim())) e.phone = '8 chiffres requis';
    if (!form.address.trim()) e.address = 'Adresse requise';
    setErrors(e);
    return e;
  };

  // Brings an invalid field into view and focuses it — the order button is in a sticky
  // bar, so without this a failed submit gives no visible feedback.
  const focusField = (name) => {
    const el = name === 'phone' ? phoneInputRef.current : addressInputRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus once the scroll is under way, so the mobile keyboard doesn't cut it short.
    setTimeout(() => el.focus({ preventScroll: true }), 300);
  };

  // One source of truth for the order lines, so the patch sent on confirmation
  // carries the same pack/quantity/price breakdown as the initial creation.
  const buildOrderLines = () => {
    const unitHt = unitPrice / 1.19;
    const itemHt = unitHt * quantity;
    const itemTva = itemHt * 0.19;
    return [{
      productVariantId: selectedVariant?.id || null,
      productId: product.id,
      quantite: Number(quantity),
      prixUnitaire: Number(lineUnitPrice),
      totalTTC: Number((unitPrice * quantity).toFixed(2)),
      totalHt: Number(itemHt.toFixed(2)),
      totalTva: Number(itemTva.toFixed(2)),
      tauxTva: 19,
      tauxRemise,
      totalRemise: Number(totalRemise.toFixed(2)),
    }];
  };

  const buildOrderData = ({ abandoned = false, phoneOverride } = {}) => {
    return {
      entet: {
        dateBonCommande: new Date().toISOString(),
        reference: referenceRef.current || `CMD-${Date.now()}`,
        utilisateurId: null,
        totalTTC: Number(total.toFixed(2)),
        totalHt: Number((total / 1.19).toFixed(2)),
        totalTva: Number((total - total / 1.19).toFixed(2)),
        totalRemise: Number(totalRemise.toFixed(2)),
        codePromo: null,
        source: 'web_store',
        etat: abandoned ? 'abandoned' : 'pending',
        fraisLivraison: Number(finalDeliveryFee.toFixed(2)),
        plateformId: null,
        clientFirstName: abandoned ? '' : (form.name || ''),
        clientLastName: '',
        clientNumber: phoneOverride || form.phone,
        clientAdresse: abandoned ? '' : form.address,
        clientEmail: '',
        ville: abandoned ? '' : form.city,
        notes: '',
      },
      line: buildOrderLines(),
    };
  };

  // Lead capture: an "abandoned" order holding just the phone number, upgraded to a
  // real order by the patch below once the customer confirms.
  const createAbandonedOrder = async (phoneInput) => {
    try {
      const res = await createOrder(buildOrderData({ abandoned: true, phoneOverride: phoneInput }));
      setAbandonedOrderId(res.id);

      // Facebook: the customer started the order (InitiateCheckout) and left a phone (Lead).
      const pixelData = {
        orderId: res.id,
        productId: product.id,
        quantity,
        price: unitPrice,
        value: total,
        currency: 'TND',
        customer: pixelCustomer({ ...form, phone: phoneInput }),
      };
      facebookConversionService.trackInitiateCheckout(pixelData, product.categorie).catch(() => {});
      facebookConversionService.trackLead(pixelData, product.categorie).catch(() => {});
    } catch (err) {
      console.error('Failed to create abandoned order:', err);
      // Let a later attempt retry — the submit path falls back to a fresh order anyway.
      abandonedOrderCreatedRef.current = false;
    }
  };

  const buildPatchData = () => ({
    id: abandonedOrderId,
    clientFirstName: form.name || '',
    clientLastName: '',
    clientEmail: '',
    clientAdresse: form.address,
    clientNumber: form.phone,
    ville: form.city,
    etat: 'pending',
    fraisDeLivraison: Number(finalDeliveryFee.toFixed(2)),
    productVariantId: selectedVariant?.id || null,
    productId: product.id,
    quantite: Number(quantity),
    totalTTC: Number(total.toFixed(2)),
    tauxTva: 19,
    totalHt: Number((total / 1.19).toFixed(2)),
    totalTva: Number((total - total / 1.19).toFixed(2)),
    totalRemise: Number(totalRemise.toFixed(2)),
    tauxRemise,
    codePromo: null,
    // Must be sent: the order was created before the customer picked a pack or
    // changed the quantity, and the backend rewrites the lines from this list.
    lines: buildOrderLines(),
  });

  const handleBuyNow = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      focusField(validationErrors.phone ? 'phone' : 'address');
      return;
    }
    setIsSubmitting(true);
    try {
      // Upgrade the abandoned order created on phone entry; otherwise create one now.
      let orderId;
      if (abandonedOrderId) {
        await updateOrderProduct(buildPatchData());
        orderId = abandonedOrderId;
      } else {
        await fetchOrderRef();
        const result = await createOrder(buildOrderData());
        orderId = result.id;
      }
      // Facebook Purchase on the product's category pixel (fire-and-forget: never blocks the order).
      facebookConversionService.trackPurchase(
        {
          orderId,
          value: total,
          currency: 'TND',
          items: [{ productId: product.id, quantity, price: unitPrice }],
          customer: pixelCustomer(form),
        },
        product.categorie
      ).catch(() => {});
      navigate(`/order-confirmation/${orderId}`);
    } catch (err) {
      console.error('Quick order failed:', err);
      setErrors((er) => ({ ...er, submit: "Une erreur est survenue, réessayez." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToCartClick = () => {
    if (onAddToCart) onAddToCart();
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const telHref = storePhone ? `tel:${storePhone.replace(/\s+/g, '')}` : null;

  return (
    <div className="font-sans pb-24 md:pb-0" dir="rtl">
      <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{product.name}</h1>

      <div className="mt-2 flex items-center gap-1 text-[#ff4d00]">
        {[...Array(5)].map((_, i) => (
          <svg key={i} className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.075 10.1c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        ))}
      </div>

      <div className="mt-3 flex items-baseline gap-3">
        {originalPrice > 0 && (
          <span className="rounded bg-green-50 px-2 py-0.5 text-sm font-semibold text-green-600">
            -{discountPct}%
          </span>
        )}
        {originalPrice > 0 && (
          <span className="text-lg text-gray-400 line-through">{formatPrice(originalPrice)} د.ت</span>
        )}
        <span className="text-2xl font-bold text-gray-900">{formatPrice(unitPrice)} د.ت</span>
      </div>

      {/* Description right under the price (dir="auto": French or Arabic text both read correctly) */}
      {product.description && product.description.trim() && (
        <p dir="auto" className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600">
          {product.description}
        </p>
      )}

      {/* Product gallery — mobile only, right under the price; desktop shows it in the side column. */}
      {gallery && <div className="mt-4 lg:hidden">{gallery}</div>}

      {storePhone && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#F3F0FF] px-4 py-2.5 text-sm text-[#5B4FCF]">
          <svg className="h-4 w-4 shrink-0 text-[#5B4FCF]" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
          </svg>
          <span>لمزيد من الإرشادات إتصل بنا على الرقم {storePhone}</span>
        </div>
      )}

      {!selectedPack && variantTypes && variantTypes.length > 0 && product.productVariants?.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {product.productVariants.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => onVariantChange && onVariantChange(v.id)}
              className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                selectedVariant?.id === v.id
                  ? 'border-[#ff4d00] bg-[#fff1ea] text-[#ff4d00] font-semibold'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {v.name}
            </button>
          ))}
        </div>
      )}

      {/* Pack offers — gold frame with a pulsing halo, so the offers catch the eye */}
      {product.hasPack && product.packProducts && product.packProducts.length > 0 && (
        <div className="relative mt-5 rounded-2xl border-2 border-gold bg-gradient-to-b from-[#FFFBEA] to-white p-4 animate-gold-glow motion-reduce:animate-none">
          <span className="absolute -top-3 right-4 rounded-full bg-gradient-to-r from-gold-dark via-gold to-gold-light bg-[length:200%_auto] px-3 py-0.5 text-[11px] font-extrabold text-white shadow-md animate-gold-shine motion-reduce:animate-none">
            عرض خاص
          </span>
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gold-dark">
            <svg className="h-4 w-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a1 1 0 01.894.553l1.382 2.764 3.05.443a1 1 0 01.555 1.705l-2.207 2.152.521 3.038a1 1 0 01-1.451 1.054L10 12.347l-2.744 1.362a1 1 0 01-1.45-1.054l.52-3.038-2.207-2.152a1 1 0 01.555-1.705l3.05-.443L9.106 2.553A1 1 0 0110 2z" />
            </svg>
            عروض الباقة
          </h3>
          <div className="space-y-2.5">
            {product.packProducts.map((pack, idx) => {
              const isSelected = selectedPack && selectedPack.quantity === pack.quantity;
              const totalPackPrice = pack.prixPack * pack.quantity;
              const totalNormalPrice = (product.prixVenteTTC || 0) * pack.quantity;
              const totalSavings = totalNormalPrice - totalPackPrice;
              const savingsPercent = totalNormalPrice > 0 ? Math.round((totalSavings / totalNormalPrice) * 100) : 0;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handlePackClick(pack)}
                  className={`relative block w-full rounded-xl border-2 bg-white p-3.5 text-right transition-all duration-200 ${
                    isSelected ? 'border-[#ff4d00] ring-1 ring-[#ff4d00] shadow-md' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {savingsPercent > 0 && (
                    <span className="absolute -top-2.5 left-3 rounded-full bg-green-500 px-2.5 py-0.5 text-[11px] font-extrabold text-white shadow-sm">
                      -{savingsPercent}%
                    </span>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${isSelected ? 'border-[#ff4d00]' : 'border-gray-300'}`}>
                        {isSelected && <span className="h-2.5 w-2.5 rounded-full bg-[#ff4d00]" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-gray-900">اشتري {pack.quantity} قطع</span>
                        <span className="block text-xs text-gray-500">{formatPrice(pack.prixPack)} د.ت للقطعة</span>
                      </span>
                    </div>
                    <div className="flex shrink-0 items-baseline gap-2">
                      {totalSavings > 0 && (
                        <span className="text-xs text-gray-400 line-through">{formatPrice(totalNormalPrice)} د.ت</span>
                      )}
                      <span className="text-lg font-extrabold text-gray-900">{formatPrice(totalPackPrice)} د.ت</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <form onSubmit={handleBuyNow} id="quickbuy-form" className="mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Nom (الاسم)</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Votre nom"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Téléphone (رقم الهاتف) <span className="text-red-500">*</span>
          </label>
          {/* LTR so the +216 prefix stays on the left, as on the main checkout form */}
          <div className="relative" dir="ltr">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex select-none items-center gap-1.5 pl-3">
              <img
                src="https://flagcdn.com/w40/tn.png"
                alt="Tunisie"
                className="h-4 w-6 rounded-sm object-cover"
                draggable={false}
              />
              <span className="text-sm font-semibold text-gray-900">+216</span>
              <span className="ml-1 h-5 border-l border-gray-300" />
            </div>
            <input
              type="tel"
              name="phone"
              ref={phoneInputRef}
              value={form.phone}
              onChange={handleChange}
              inputMode="numeric"
              maxLength={8}
              placeholder="Votre numéro (8 chiffres)"
              className={`w-full rounded-lg border py-2.5 pl-[6.5rem] pr-3 text-left text-sm text-gray-900 outline-none focus:ring-1 ${
                errors.phone ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#ff4d00] focus:ring-[#ff4d00]'
              }`}
            />
          </div>
          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Adresse (العنوان) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="address"
            ref={addressInputRef}
            value={form.address}
            onChange={handleChange}
            placeholder="Adresse complète"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-1 ${
              errors.address ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#ff4d00] focus:ring-[#ff4d00]'
            }`}
          />
          {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Ville (المدينة)</label>
          <select
            name="city"
            value={form.city}
            onChange={handleChange}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]"
          >
            {tunisianCities.map((c) => (
              <option key={c.Code} value={c.Code}>{c.EnName}</option>
            ))}
          </select>
        </div>

        {selectedPack ? (
          <div className="flex items-center justify-between rounded-lg bg-[#fff1ea] px-4 py-2.5 text-sm">
            <button
              type="button"
              onClick={() => handlePackClick(selectedPack)}
              className="font-medium text-[#ff4d00] underline"
            >
              إلغاء الباقة
            </button>
            <span className="font-semibold text-gray-800">الكمية: {quantity} (باقة)</span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onQuantityChange(quantity + 1)}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >+</button>
            <span className="w-6 text-center text-base font-semibold text-gray-900">{quantity}</span>
            <button
              type="button"
              onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >−</button>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-lg bg-[#4983a9] py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#3f7294] disabled:opacity-60"
        >
          {isSubmitting ? 'Envoi en cours…' : 'اشتري الآن'}
        </button>
        {errors.submit && <p className="text-center text-sm text-red-500">{errors.submit}</p>}

        {!selectedPack && (
          <button
            type="button"
            onClick={handleAddToCartClick}
            className="w-full rounded-lg border border-[#4983a9] py-3.5 text-base font-semibold text-[#4983a9] transition-colors hover:bg-[#4983a9]/5"
          >
            {addedToCart ? 'Ajouté ✓' : 'أضف إلى السلة'}
          </button>
        )}

        <div className="rounded-lg bg-gray-50 p-4">
          <div className="flex justify-between py-1 text-sm text-gray-600">
            <span>{formatPrice(subtotal)}</span>
            <span>المجموع الفرعي</span>
          </div>
          <div className="flex justify-between py-1 text-sm text-gray-600">
            <span>{freeDelivery ? 'مجاني' : formatPrice(finalDeliveryFee)}</span>
            <span>التوصيل</span>
          </div>
          {packTotalRemise > 0 && (
            <div className="flex justify-between py-1 text-sm font-semibold text-green-600">
              <span>-{formatPrice(packTotalRemise)}</span>
              <span>توفير الباقة</span>
            </div>
          )}
          {exitTotalRemise > 0 && (
            <div className="flex justify-between py-1 text-sm font-semibold text-green-600">
              <span>-{formatPrice(exitTotalRemise)}</span>
              <span>خصم العرض الخاص ({exitPct}%)</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t border-gray-200 pt-2 text-base font-bold text-gray-900">
            <span>{formatPrice(total)}</span>
            <span>المجموع</span>
          </div>
        </div>
      </form>

      {/* Sticky mobile bar — the order button stays reachable while scrolling the product page,
          with a one-tap call button next to it for shoppers who'd rather ask before ordering. */}
      <div
        dir="ltr"
        className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-gray-200 bg-white p-3 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] md:hidden"
      >
        {telHref && (
          <a
            href={telHref}
            aria-label="Appeler"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-700 active:bg-gray-50"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </a>
        )}
        <button
          type="submit"
          form="quickbuy-form"
          disabled={isSubmitting}
          className="flex-1 rounded-lg bg-[#4983a9] py-3 text-base font-semibold text-white transition-colors hover:bg-[#3f7294] disabled:opacity-60"
        >
          {isSubmitting ? 'Envoi en cours…' : 'Commander'}
        </button>
      </div>

      {/* "Remise" offered when the visitor is about to leave; accepted, it lowers the price
          and brings the order form into view. */}
      <ExitOfferPopup
        productId={product.id}
        percent={exitOfferAccepted ? 0 : exitOfferPercent}
        price={basePrice}
        onAccept={() => {
          setExitOfferAccepted(true);
          phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      />
    </div>
  );
};

export default QuickBuyProductInfo;
